import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { organizationTypePositionService } from "../services/organization-type-position.service";
import type {
	CreateOrganizationTypePositionRequest,
	ErrorResponse,
	PaginationParams,
	UpdateOrganizationTypePositionRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const orgTypePositionKeys = {
	all: ["organization-type-positions"] as const,
	lists: () => [...orgTypePositionKeys.all, "list"] as const,
	list: (params?: PaginationParams) => [...orgTypePositionKeys.lists(), params] as const,
	details: () => [...orgTypePositionKeys.all, "detail"] as const,
	detail: (id: string) => [...orgTypePositionKeys.details(), id] as const,
};

// ─── useOrganizationTypePositions (list) ─────────────────────────────
export function useOrganizationTypePositions(
	params?: PaginationParams,
	options?: { enabled?: boolean }
) {
	return useQuery({
		queryKey: orgTypePositionKeys.list(params),
		queryFn: () => organizationTypePositionService.list(params),
		enabled: options?.enabled ?? true,
	});
}

// ─── useOrganizationTypePosition (getById) ───────────────────────────
export function useOrganizationTypePosition(id: string) {
	return useQuery({
		queryKey: orgTypePositionKeys.detail(id),
		queryFn: () => organizationTypePositionService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateOrganizationTypePosition ───────────────────────────────
export function useCreateOrganizationTypePosition() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateOrganizationTypePositionRequest) =>
			organizationTypePositionService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: orgTypePositionKeys.lists(),
			});
			message.success("Lavozim muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Lavozim yaratishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateOrganizationTypePosition ───────────────────────────────
export function useUpdateOrganizationTypePosition() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationTypePositionRequest }) =>
			organizationTypePositionService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: orgTypePositionKeys.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: orgTypePositionKeys.detail(variables.id),
			});
			message.success("Lavozim muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Lavozim yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteOrganizationTypePosition ───────────────────────────────
export function useDeleteOrganizationTypePosition() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => organizationTypePositionService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: orgTypePositionKeys.lists(),
			});
			message.success("Lavozim muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Lavozim o'chirishda xatolik";
			message.error(msg);
		},
	});
}
